// De SSE-events van de workspace-chat vertaald naar viewdata.
//
// De events komen uit een plain Django view en zijn snake_case; overal elders in
// de frontend is het camelCase. Dat verschil hoort op één plek te bestaan, en
// dit is die plek: componenten krijgen getypeerde waarden en zien nooit een
// ruwe payload. De functies hier zijn puur, zodat ze zonder DOM te testen zijn.

import { stripProposalTags } from "../../chat/tags";
import { FilterSuggestion, ReportProposal, ResearchProposal } from "./types";

// Ruimte voor latere veilige toolstatussen ("bron zoeken", "document lezen").
// De server stuurt dit veld nog niet; het contract staat er alvast zodat een
// volgende MR alleen een event hoeft toe te voegen.
export interface ChatStatus {
  kind: string;
  label?: string;
}

export type ChatEvent =
  | { type: "session"; sessionId: number; assistantMessageId?: number; title?: string }
  | { type: "content"; text: string }
  | { type: "filter"; filter: FilterSuggestion }
  | { type: "researchProposal"; proposal: ResearchProposal }
  | { type: "reportProposal"; proposal: ReportProposal }
  | { type: "assistantDeleted" }
  | { type: "status"; status: ChatStatus };

/**
 * Eén ruwe payload naar een getypeerd event, of `undefined` als we hem niet
 * kennen. Een onbekend eventtype is geen fout: de server mag er een toevoegen
 * zonder dat een oudere frontend de sessie laat kantelen.
 */
export function normaliseEvent(data: any): ChatEvent | undefined {
  if (!data || typeof data !== "object") return undefined;
  if (data.session_id) {
    return {
      type: "session",
      sessionId: data.session_id,
      assistantMessageId: data.assistant_message_id,
      title: data.title,
    };
  }
  if (data.content) return { type: "content", text: data.content };
  if (data.filter) {
    return {
      type: "filter",
      filter: {
        id: data.filter.id,
        criteria: data.filter.criteria,
        label: data.filter.label,
        total: data.filter.total,
        capped: data.filter.capped,
        created: data.filter.created,
      },
    };
  }
  if (data.research_proposal) {
    return {
      type: "researchProposal",
      proposal: {
        id: data.research_proposal.id,
        parcelId: data.research_proposal.parcel_id,
        parcelName: data.research_proposal.parcel_name,
        extraQuestion: data.research_proposal.extra_question,
        researchId: data.research_proposal.research_id,
        created: data.research_proposal.created,
      },
    };
  }
  if (data.report_proposal) {
    return {
      type: "reportProposal",
      proposal: {
        id: data.report_proposal.id,
        title: data.report_proposal.title,
        situation: data.report_proposal.situation,
        conclusion: data.report_proposal.conclusion,
        fileId: data.report_proposal.file_id,
        fileUrl: data.report_proposal.file_url,
        created: data.report_proposal.created,
      },
    };
  }
  if (data.assistant_deleted) return { type: "assistantDeleted" };
  if (data.status?.kind) {
    return { type: "status", status: { kind: data.status.kind, label: data.status.label } };
  }
  return undefined;
}

export interface ChatStream {
  sessionId?: number;
  assistantMessageId?: number;
  // Ruw én getoond: de <filters>-, <research>- en <report>-tags mogen niet in beeld komen,
  // maar de server strip't ze pas bij het opslaan. Tijdens het streamen moeten
  // we dus zelf knippen, en dat kan alleen over de volledige ruwe tekst.
  raw: string;
  content: string;
  filters: FilterSuggestion[];
  researchProposals: ResearchProposal[];
  reportProposals: ReportProposal[];
  deleted: boolean;
  finished: boolean;
  status?: ChatStatus;
}

export const emptyChatStream = (): ChatStream => ({
  raw: "",
  content: "",
  filters: [],
  researchProposals: [],
  reportProposals: [],
  deleted: false,
  finished: true,
});

/** Eén event toepassen; een onbekend event laat de stroom ongemoeid. */
export function applyChatEvent(stream: ChatStream, data: any): ChatStream {
  const event = normaliseEvent(data);
  if (!event) return stream;
  switch (event.type) {
    case "session":
      return {
        ...stream,
        sessionId: event.sessionId,
        assistantMessageId: event.assistantMessageId,
      };
    case "content": {
      // Tekst die binnenkomt ís het bewijs dat de tool klaar is, dus de status
      // verdwijnt hier. Zonder dit blijft "Projectbronnen doorzoeken" staan
      // zodra iemand hem gaat tonen -- de server stuurt geen afsluitend event,
      // en dat hoeft ook niet.
      const raw = stream.raw + event.text;
      return {
        ...stream,
        raw,
        content: stripProposalTags(raw),
        status: undefined,
      };
    }
    case "filter":
      return { ...stream, filters: [...stream.filters, event.filter] };
    case "researchProposal":
      return { ...stream, researchProposals: [...stream.researchProposals, event.proposal] };
    case "reportProposal":
      return { ...stream, reportProposals: [...stream.reportProposals, event.proposal] };
    case "assistantDeleted":
      return { ...stream, deleted: true };
    case "status":
      return { ...stream, status: event.status };
  }
}

/**
 * De gebruiker drukte op stop, of de verbinding viel weg. Wat er al binnen was
 * blijft staan: de server bewaart hetzelfde gedeeltelijke antwoord met
 * `finished=False`, en het weggooien zou het gesprek anders maken dan de
 * historie die na een refresh terugkomt.
 */
export const stopChatStream = (stream: ChatStream): ChatStream => ({
  ...stream,
  finished: false,
});

/** Een mislukte stream: liever het halve antwoord dan een lege bel. */
export const failChatStream = (stream: ChatStream, message: string): ChatStream => ({
  ...stream,
  content: stream.content || message,
});
